// Client for the fca-filter-ai image-analysis endpoints. Same shape as `assetTemplateService.js` and
// `filterAssistant/core/ottoAccess.js`: baseURL + withCredentials so the *.fcatracker.com session cookie
// flows, plus the shared 401 refresh-and-replay.
//
// A feature-local axios instance is the right call here even though the house rule says not to make them:
// that rule is about the app's THREE backends (Ruby / FastAPI / Django), reached through
// `authorizationService.js`. filter-ai is a fourth service with its own origin and auth gate, and every
// caller of it in this app already does exactly this.
//
// HOW THE CLIENT IS LOADED — three versions, each broke something, and the constraints are why:
//
//   1. `import axios` + `axios.create()` at module scope. Image Management is embedded in the region / site /
//      building / asset pages, so creating the client at import ran `attachOttoAuthRetry` during import and
//      six jest suites that mock axios died with "Cannot read properties of undefined (reading 'response')".
//
//   2. Lazy `require("axios").default || require("axios")` (copied from news/newsStore.js). Fine in jest,
//      broken in the REAL BROWSER: under CRA 5 / webpack 5 that require yields an object with no `create`,
//      so every Analyse click threw "axios.create is not a function" before a request was sent. Jest could
//      not see it because the modal test mocks this whole module. (newsStore.js has the same defect.)
//
//   3. Static `import axios` + lazy create. Fine in the browser, broken in JEST: axios 1.x is ESM, CRA's jest
//      does not transform node_modules, so any suite that loads this module without mocking it fails with
//      "Cannot use import statement outside a module" — and ImageMain is loaded by a lot of suites.
//
// So: a DYNAMIC `import()` on first use. Webpack resolves it as a real ES module (namespace `.default` is
// the axios instance), and under jest nothing is imported until a call is made — which no suite that merely
// renders a page ever does. Verified in the real browser with e2e/image-analysis-probe.spec.js, which
// asserts the POST goes out and returns 200; that probe is the only thing that caught version 2.
//
// LONG timeout, deliberately. One request analyses up to 25 photographs, each fetched from S3, downscaled
// and sent to a vision model — a full batch legitimately runs into the tens of seconds, and a short timeout
// would abort the request while the work it paid for completes server-side.
let clientPromise = null;

const getClient = () => {
    if (!clientPromise) {
        clientPromise = Promise.all([
            import("axios"),
            import("../../config/constants"),
            import("../filterAssistant/core/authRetry")
        ])
            .then(([axiosModule, constants, authRetry]) => {
                const axios = axiosModule.default || axiosModule;
                const client = axios.create({
                    baseURL: constants.FILTER_AI_API_URL,
                    timeout: 180000,
                    withCredentials: true
                });
                authRetry.attachOttoAuthRetry(client);
                return client;
            })
            .catch(err => {
                // Do not cache a failed load: a transient chunk-load failure must not break Analyse for the
                // rest of the session.
                clientPromise = null;
                throw err;
            });
    }
    return clientPromise;
};

// Each call binds the client to a local named `imageAnalysisApi` and calls it with a PLAIN STRING LITERAL
// path. That exact shape — `<name>Api.post("/path"` — is what the otto-sync `xrepo-proxy-paths` gate's
// regex extracts, so these endpoints are checked against the deployed nginx allowlist
// (`filter-ai/deploy/frontend-proxy-paths.json`). Call `getClient().post(...)` directly, or build the path
// from a variable, and the gate silently stops seeing it — and an unproxied path does not 404 in a deployed
// environment, it falls through to the SPA and returns index.html.
export const analyzeImages = async (imageUploadIds, options = {}) => {
    const imageAnalysisApi = await getClient();
    return imageAnalysisApi.post("/image_analysis/analyze", {
        image_upload_ids: imageUploadIds,
        // Omitted unless a caller overrides them, so production uses the backend's configured model and
        // resolution rather than pinning a copy here that would drift the next time either moves. The page's
        // model selector passes one; the backend refuses anything outside its own allowlist.
        ...(options.model ? { model: options.model } : {}),
        ...(options.maxEdge ? { max_edge: options.maxEdge } : {})
    });
};

export const getVocabulary = async () => {
    const imageAnalysisApi = await getClient();
    return imageAnalysisApi.get("/image_analysis/vocabulary");
};

// The models this page may be pointed at, with the benchmark numbers behind each. Costs come back only for
// super_admins - `pricing_visible` says which answer this was.
export const getModels = async () => {
    const imageAnalysisApi = await getClient();
    return imageAnalysisApi.get("/image_analysis/models");
};

// Which of a page of photos Otto has already read, for the grid's badge. Deliberately not `lookupSavedResults`:
// that carries every full reading, which is hundreds of KB per page of images to draw an icon.
export const lookupAnalysed = async imageUploadIds => {
    const imageAnalysisApi = await getClient();
    return imageAnalysisApi.post("/image_analysis/analysed", { image_upload_ids: imageUploadIds });
};

// The saved reading for one photo (the viewer's AI Analyze tab). `record` is null when never analysed.
export const getSavedResult = async imageUploadId => {
    const imageAnalysisApi = await getClient();
    return imageAnalysisApi.get(`/image_analysis/results/${encodeURIComponent(imageUploadId)}`);
};

// Saved readings for a page of photos. A POST only because the ids cannot travel in a URL; it writes nothing.
export const lookupSavedResults = async imageUploadIds => {
    const imageAnalysisApi = await getClient();
    return imageAnalysisApi.post("/image_analysis/results", { image_upload_ids: imageUploadIds });
};

// What a run would do - new vs already analysed, and the estimated cost for the model it would use - BEFORE
// anything is spent.
export const previewRun = async (imageUploadIds, reanalyse = false, model = null) => {
    const imageAnalysisApi = await getClient();
    return imageAnalysisApi.post("/image_analysis/runs/preview", {
        image_upload_ids: imageUploadIds,
        reanalyse,
        ...(model ? { model } : {})
    });
};

// Start a background run. Answers with the run ALREADY in flight when there is one (`already_running`).
export const startRun = async (imageUploadIds, reanalyse = false, model = null) => {
    const imageAnalysisApi = await getClient();
    return imageAnalysisApi.post("/image_analysis/runs", {
        image_upload_ids: imageUploadIds,
        reanalyse,
        ...(model ? { model } : {})
    });
};

export const getActiveRun = async () => {
    const imageAnalysisApi = await getClient();
    return imageAnalysisApi.get("/image_analysis/runs/active");
};

export const getRun = async runId => {
    const imageAnalysisApi = await getClient();
    return imageAnalysisApi.get(`/image_analysis/runs/${runId}`);
};

export const cancelRun = async runId => {
    const imageAnalysisApi = await getClient();
    return imageAnalysisApi.post(`/image_analysis/runs/${runId}/cancel`);
};

// For tests: drop the cached client so a re-mocked axios is picked up.
export const __resetImageAnalysisClient = () => {
    clientPromise = null;
};
